var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useContext, useEffect } from 'react';
import { UNSAFE_NavigationContext as NavigationContext, } from 'react-router-dom';
import { appActions } from 'app/app.actions';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
/**
 * A hook that blocks navigation attempts. This is useful for preventing leaving
 * a page until some condition is met, like saving form data.
 *
 * This implementation is based on the now removed `useBlocker` and `usePrompt`
 * hooks from react-router-dom. As things stand, it doesn't seem react-router
 * will be adding these back, so we have to maintain these ourselves or consider
 * other implementations, because these hooks rely on `navigator.block` method
 * which is only available with `unstable_HistoryRouter` router.
 *
 * @param when Boolean indicating whether the prompt should be trigger upon
 *   navigation.
 * @param message Custom text message to display in the prompt.
 *
 *   See below links for more context:
 * @see https://github.com/remix-run/react-router/issues/8139
 * @see https://github.com/remix-run/react-router/issues/8139#issuecomment-1247080906
 */
export const useNavigatePrompt = (when = true, message = 'When you leave this page, all unsaved changes will be lost.') => {
    const dispatch = useAppDispatch();
    const blocker = useCallback((tx) => __awaiter(void 0, void 0, void 0, function* () {
        const { isConfirmed } = yield dispatch(appActions.showModal({
            modal: {
                type: 'app:confirm',
                arg: {
                    title: 'Sure you want to leave?',
                    description: message,
                    confirm: {
                        label: 'Leave',
                        variant: 'primary',
                    },
                },
            },
        }));
        if (isConfirmed) {
            tx.retry();
        }
    }), [dispatch, message]);
    useNavigateBlocker(blocker, when);
};
const useNavigateBlocker = (blocker, when = true) => {
    const { navigator } = useContext(NavigationContext);
    useEffect(() => {
        if (!when)
            return;
        const unblock = navigator.block((tx) => {
            const autoUnblockingTx = Object.assign(Object.assign({}, tx), { retry() {
                    unblock();
                    tx.retry();
                } });
            blocker(autoUnblockingTx);
        });
        return unblock;
    }, [navigator, blocker, when]);
};
